import { toast } from "sonner";
import { create } from "zustand";
import { desktop } from "@/desktop/bridge";
import { queryClient } from "./queries";

/**
 * Atualizar (Ctrl+R, botão ⟳ na barra de título): busca de novo tudo o que está na tela — projetos,
 * mensagens, membros, notificações, plugins — e reconecta o que caiu, sem recarregar a janela nem
 * perder o que está aberto (editores, terminal, rascunhos). "Recarregar janela" (Ctrl+Shift+R)
 * continua disponível para o caso raro de precisar começar do zero.
 */
export const useRefreshing = create<{ busy: boolean }>(() => ({ busy: false }));

/** Quem tem conexão própria (sala do projeto, chat) se reconecta quando o Atualizar pede. */
const reconnectors = new Set<() => void>();
export function onRefresh(fn: () => void): () => void {
  reconnectors.add(fn);
  return () => reconnectors.delete(fn);
}

export async function refreshApp() {
  if (useRefreshing.getState().busy) return;
  useRefreshing.setState({ busy: true });
  const started = Date.now();
  try {
    for (const fn of reconnectors) {
      try {
        fn();
      } catch {
        // uma conexão que não reconecta não impede o resto de atualizar
      }
    }
    await queryClient.refetchQueries({ type: "active" });
    // Os dados fora da tela também ficam velhos: buscados de novo quando aparecerem.
    void queryClient.invalidateQueries({ type: "inactive", refetchType: "none" });
    toast.success("Atualizado", { duration: 1500 });
  } catch {
    toast.error("Não deu para atualizar tudo. Confira a conexão.");
  } finally {
    // O ícone gira pelo menos um pouco: dá para ver que algo aconteceu.
    setTimeout(() => useRefreshing.setState({ busy: false }), Math.max(0, 450 - (Date.now() - started)));
  }
}

/** Recarregar a janela inteira (como o F5 de um navegador). */
export function reloadWindow() {
  if (desktop) desktop.runRole("reload");
  else window.location.reload();
}
